import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RefreshCw, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import API from '../../../util/API';
import ADMIN_PATHS from '../ADMIN_PATHS';
import { formatMoney as fmtMoney, normalizeCurrencyRates } from '../../../util/money';

// Status vocabulary enforced by ReturnRefund.model.js (status enum).
const REQUEST_STATUSES = ['Pending', 'Approved', 'Rejected', 'Processing', 'Completed', 'Refunded'];

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const cls =
    s === 'refunded' || s === 'completed'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : s === 'approved' || s === 'processing'
        ? 'border-sky-200 bg-sky-50 text-sky-700'
        : s === 'rejected'
          ? 'border-rose-200 bg-rose-50 text-rose-700'
          : s === 'pending'
            ? 'border-amber-200 bg-amber-50 text-amber-700'
            : 'border-slate-200 bg-slate-50 text-slate-700';

  return (
    <span className={cx('inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold', cls)}>
      {status}
    </span>
  );
};

const getOrderRef = (request) => {
  const order = request?.order;
  if (order && typeof order === 'object') {
    return String(order._id || order.orderId || '');
  }
  return String(order || request?.orderId || '');
};

const RefundsInner = () => {
  const { currency: selectedCurrency = 'USD' } = useSelector((state) => state.userSettings || {});
  const currencyPayload = useSelector((state) => state.currency?.currency_list);
  const rates = useMemo(() => normalizeCurrencyRates(currencyPayload), [currencyPayload]);
  const money = (amount, from = 'USD') => fmtMoney(amount, { from, to: selectedCurrency || 'USD', rates });

  const [requests, setRequests] = useState([]);
  const [pageStatus, setPageStatus] = useState('idle'); // idle | loading | failed | succeeded
  const [loadError, setLoadError] = useState('');
  const [savingId, setSavingId] = useState('');
  const [statusDrafts, setStatusDrafts] = useState({});

  const loadRequests = useCallback(async () => {
    setPageStatus('loading');
    setLoadError('');
    try {
      const res = await API.get('/return-refund');
      setRequests(Array.isArray(res?.data?.data) ? res.data.data : []);
      setPageStatus('succeeded');
    } catch (err) {
      setRequests([]);
      setPageStatus('failed');
      setLoadError(
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Failed to load refund requests'
      );
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleStatusSave = async (request) => {
    const nextStatus = String(statusDrafts[request._id] || '').trim();
    if (!nextStatus || nextStatus === request.status) return;

    setSavingId(request._id);
    try {
      const res = await API.put(`/return-refund/${request._id}`, { status: nextStatus });
      const updated = res?.data?.data;
      setRequests((prev) =>
        prev.map((row) =>
          row._id === request._id
            ? { ...row, status: updated?.status || nextStatus, updatedAt: updated?.updatedAt || row.updatedAt }
            : row
        )
      );
      setStatusDrafts((prev) => {
        const next = { ...prev };
        delete next[request._id];
        return next;
      });
      toast.success(`Request marked ${updated?.status || nextStatus}. Remember: status changes do not move money.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Failed to update request status'
      );
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <SectionHead
        title="Refunds"
        subtitle="Customer return/refund requests (GET /return-refund). Status changes are recorded via PUT /return-refund/:id."
        right={
          <button
            type="button"
            onClick={loadRequests}
            disabled={pageStatus === 'loading'}
            className={cx(
              'inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-primary',
              pageStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            <RefreshCw className={cx('w-4 h-4', pageStatus === 'loading' ? 'animate-spin' : '')} />
            Refresh
          </button>
        }
      />

      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-900">
        Marking a request “Refunded” records the workflow state only — no payment refund is
        triggered, because there is no refund API on the gateway side. Move the money manually in
        the payment provider first, then record it here. The former “Process” button was removed:
        its action has no backend endpoint.
      </div>

      {pageStatus === 'failed' ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {loadError}
        </div>
      ) : null}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Request</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Requested amount</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageStatus === 'loading' ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-sm text-slate-500">
                    Loading return/refund requests…
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-sm text-slate-500">
                    {pageStatus === 'failed' ? 'Could not load requests.' : 'No return/refund requests yet.'}
                  </td>
                </tr>
              ) : (
                requests.map((request) => {
                  const orderRef = getOrderRef(request);
                  const draft = statusDrafts[request._id] ?? request.status;
                  const dirty = draft !== request.status;
                  const amount = Number(request?.refund?.amount || 0);

                  return (
                    <tr key={request._id} className="hover:bg-slate-50 transition-colors align-top">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-primary break-all">
                        {String(request._id).slice(-8)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {orderRef ? (
                          <Link
                            to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${orderRef}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            {String(request.orderId || request.order?.orderId || orderRef).slice(0, 24)}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{request?.user?.name || '—'}</p>
                        {request?.user?.email ? <p className="text-xs text-slate-500 break-all">{request.user.email}</p> : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <p className="line-clamp-2">{request?.productName || request?.product?.name || '—'}</p>
                        {request?.productId ? <p className="mt-0.5 text-xs text-slate-500 font-mono break-all">PID: {String(request.productId)}</p> : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{request?.requestType || '—'}</td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">
                        {amount > 0 ? (
                          money(amount, 'USD')
                        ) : (
                          <span className="text-xs font-semibold text-slate-500" title="ReturnRefund.refund.amount was never recorded on this request">
                            not recorded
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[240px]">
                        <p className="line-clamp-2">{request?.reason || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          <StatusPill status={request.status} />
                          <select
                            value={draft}
                            onChange={(e) =>
                              setStatusDrafts((prev) => ({ ...prev, [request._id]: e.target.value }))
                            }
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 focus:border-primary focus:ring-2 focus:ring-primary/20"
                            disabled={savingId === request._id}
                          >
                            {REQUEST_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(request?.createdAt)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          {orderRef ? (
                            <Link
                              to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${orderRef}`}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View order
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleStatusSave(request)}
                            disabled={!dirty || savingId === request._id}
                            className={cx(
                              'inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-black',
                              dirty && savingId !== request._id
                                ? 'bg-primary text-white hover:bg-primary/90'
                                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            )}
                          >
                            {savingId === request._id ? 'Saving…' : 'Save status'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const Refunds = () => <RefundsInner />;

export default Refunds;
